import React from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { STATIC_COURSE } from "../../content/course_static";
import { getAdminCourseWeekOverrides, upsertAdminCourseWeekOverride, deleteAdminCourseWeekOverride } from "../../lib/api";

function mapOverrides(items) {
  const out = {};
  for (const item of Array.isArray(items) ? items : []) {
    const w = Number(item?.week);
    if (!w) continue;
    out[w] = item;
  }
  return out;
}

export default function AdminGuideEditor() {
  const [params] = useSearchParams();
  const passcode = params.get("passcode") || "";

  const [selectedWeek, setSelectedWeek] = React.useState(1);
  const [overrideMap, setOverrideMap] = React.useState({});
  const [title, setTitle] = React.useState("");
  const [task, setTask] = React.useState("");
  const [bodyText, setBodyText] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");

  const baseWeek = React.useMemo(() => STATIC_COURSE.weeks.find((w) => w.week === selectedWeek), [selectedWeek]);
  const override = overrideMap[selectedWeek];
  const merged = React.useMemo(() => {
    if (!baseWeek) return null;
    if (!override) return baseWeek;
    return {
      ...baseWeek,
      title: override.title || baseWeek.title,
      task: override.task || baseWeek.task,
      body: Array.isArray(override.body) && override.body.length ? override.body : baseWeek.body,
    };
  }, [baseWeek, override]);

  const load = React.useCallback(async () => {
    if (!passcode) return;
    setLoading(true);
    try {
      const data = await getAdminCourseWeekOverrides(passcode);
      setOverrideMap(mapOverrides(data?.items));
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load guide overrides.");
    } finally {
      setLoading(false);
    }
  }, [passcode]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (!merged) return;
    setTitle(merged.title || "");
    setTask(merged.task || "");
    setBodyText((Array.isArray(merged.body) ? merged.body : []).join("\n\n"));
    setMessage("");
  }, [merged]);

  const onSave = async () => {
    if (!passcode) return;
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const body = String(bodyText || "")
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean);

      await upsertAdminCourseWeekOverride(selectedWeek, { title, task, body }, passcode);
      await load();
      setMessage(`Saved Week ${selectedWeek} override.`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save week override.");
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    if (!passcode) return;
    const ok = window.confirm(`Reset Week ${selectedWeek} to default text?`);
    if (!ok) return;
    setSaving(true);
    setMessage("");
    setError("");
    try {
      await deleteAdminCourseWeekOverride(selectedWeek, passcode);
      await load();
      setMessage(`Reset Week ${selectedWeek} to default.`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to reset week override.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10 space-y-6" data-testid="admin-guide-editor-page">
      <Card>
        <CardHeader>
          <CardTitle data-testid="admin-guide-editor-title">Guide Weekly Text Editor (Quick)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!passcode && <div className="text-sm text-slate-500">Missing passcode. Use /admin first.</div>}
          {!!error && <div className="text-sm text-red-600" data-testid="admin-guide-editor-error">{error}</div>}
          {!!message && <div className="text-sm text-emerald-700" data-testid="admin-guide-editor-message">{message}</div>}

          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-4" data-testid="admin-guide-editor-layout">
              <div className="rounded-xl border p-3" data-testid="admin-guide-week-list">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Weeks</div>
                <div className="space-y-1 max-h-[560px] overflow-auto">
                  {STATIC_COURSE.weeks.map((w) => {
                    const hasOverride = Boolean(overrideMap[w.week]);
                    return (
                      <button
                        key={w.week}
                        className={`w-full text-left rounded-md border px-2 py-1.5 text-sm ${selectedWeek === w.week ? "bg-emerald-600 text-white border-emerald-600" : "hover:bg-slate-50"}`}
                        onClick={() => setSelectedWeek(w.week)}
                        data-testid={`admin-guide-week-button-${w.week}`}
                      >
                        Week {w.week} {hasOverride ? "• edited" : ""}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-xl border p-4 space-y-3" data-testid="admin-guide-editor-form-card">
                <div className="text-sm text-slate-500" data-testid="admin-guide-editor-week-label">Editing Week {selectedWeek}</div>

                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="week-title">Title</label>
                  <input id="week-title" className="w-full rounded-md border px-3 py-2 text-sm" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-guide-title-input" />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="week-task">Weekly task text</label>
                  <input id="week-task" className="w-full rounded-md border px-3 py-2 text-sm" value={task} onChange={(e) => setTask(e.target.value)} data-testid="admin-guide-task-input" />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="week-body">Body paragraphs</label>
                  <textarea
                    id="week-body"
                    className="w-full min-h-[280px] rounded-md border px-3 py-2 text-sm"
                    value={bodyText}
                    onChange={(e) => setBodyText(e.target.value)}
                    data-testid="admin-guide-body-textarea"
                  />
                  <div className="text-xs text-slate-500">Tip: separate paragraphs with a blank line.</div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button type="button" variant="outline" onClick={onReset} disabled={saving} data-testid="admin-guide-reset-button">Reset to default</Button>
                  <Button type="button" onClick={onSave} disabled={saving} data-testid="admin-guide-save-button">{saving ? "Saving..." : "Save changes"}</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
